/**
 * Polling Guard — controls whether this tab should run polling.
 *
 * Three conditions must ALL be true for shouldPoll to be true:
 *   1. Tab is visible (not hidden/minimized)
 *   2. User is active (moved mouse / pressed key within IDLE_TIMEOUT)
 *   3. This tab is the elected leader (only 1 tab polls at a time)
 *
 * Usage:
 *   import { shouldPoll, onBecomeActive } from '@/composables/usePollingGuard'
 *   setInterval(() => { if (!shouldPoll.value) return; doPolling() }, 30000)
 *   onBecomeActive(() => { doPolling() })  // immediate refresh when user returns
 */
import { ref, computed, readonly } from 'vue'

const IDLE_TIMEOUT_MS = 15 * 60 * 1000   // 15 minutes
const LEADER_PING_MS = 4_000              // leader pings every 4s
const LEADER_DEAD_MS = 10_000             // assume leader dead after 10s silence

const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

// ─── Reactive state ───
const isVisible = ref(document.visibilityState === 'visible')
const isActive = ref(true)
const isLeader = ref(false)

const shouldPoll = computed(() => isVisible.value && isActive.value && isLeader.value)

// Callbacks to fire when user returns from idle
const resumeCallbacks = []

function onBecomeActive(fn) {
  resumeCallbacks.push(fn)
}

function notifyBecomeActive() {
  console.log('[PollingGuard] User returned — polling resumed')
  resumeCallbacks.forEach(fn => { try { fn() } catch (e) { /* silent */ } })
}

// ─── 1. Visibility ───
document.addEventListener('visibilitychange', () => {
  const wasVisible = isVisible.value
  isVisible.value = document.visibilityState === 'visible'
  if (!wasVisible && isVisible.value) notifyBecomeActive()
})

// ─── 2. Idle Detection ───
let idleTimer = null

function resetIdleTimer() {
  const wasIdle = !isActive.value
  isActive.value = true
  clearTimeout(idleTimer)
  idleTimer = setTimeout(() => {
    isActive.value = false
    console.log('[PollingGuard] User idle — polling paused')
  }, IDLE_TIMEOUT_MS)
  if (wasIdle) {
    notifyBecomeActive()
  }
}

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
let activityThrottle = 0

function onActivity() {
  const now = Date.now()
  if (now - activityThrottle < 5000) return
  activityThrottle = now
  resetIdleTimer()
}

ACTIVITY_EVENTS.forEach(ev => document.addEventListener(ev, onActivity, { passive: true }))
resetIdleTimer()

// ─── 3. Tab Leader Election via BroadcastChannel ───
let channel = null
let leaderPingTimer = null
let leaderWatchdog = null
let lastLeaderPing = 0

function startLeaderPing() {
  if (leaderPingTimer) return
  leaderPingTimer = setInterval(() => {
    try { channel?.postMessage({ type: 'ping', id: TAB_ID }) } catch {}
  }, LEADER_PING_MS)
}

function stopLeaderPing() {
  clearInterval(leaderPingTimer)
  leaderPingTimer = null
}

function startWatchdog() {
  if (leaderWatchdog) return
  leaderWatchdog = setInterval(() => {
    if (Date.now() - lastLeaderPing > LEADER_DEAD_MS) {
      claimLeadership()
    }
  }, LEADER_DEAD_MS / 2)
}

function claimLeadership() {
  isLeader.value = true
  lastLeaderPing = Date.now()
  startLeaderPing()
  try { channel?.postMessage({ type: 'claim', id: TAB_ID }) } catch {}
  console.log('[PollingGuard] This tab is now leader')
}

function yieldLeadership() {
  isLeader.value = false
  stopLeaderPing()
}

try {
  channel = new BroadcastChannel('techvill_tab_leader')

  channel.onmessage = (e) => {
    const msg = e.data
    if (!msg || msg.id === TAB_ID) return

    if (msg.type === 'claim') {
      if (isLeader.value && msg.id > TAB_ID) {
        yieldLeadership()
      } else if (isLeader.value) {
        channel.postMessage({ type: 'claim', id: TAB_ID })
      }
      lastLeaderPing = Date.now()
    }

    if (msg.type === 'ping') {
      lastLeaderPing = Date.now()
      if (isLeader.value && msg.id > TAB_ID) {
        yieldLeadership()
      }
    }
  }

  setTimeout(claimLeadership, Math.random() * 500 + 200)
  startWatchdog()
} catch {
  // BroadcastChannel not supported — this tab is always leader
  isLeader.value = true
  console.warn('[PollingGuard] BroadcastChannel not available — single-tab mode')
}

// When tab is closing, release leadership so other tabs can take over immediately
window.addEventListener('beforeunload', () => {
  if (isLeader.value) {
    try { channel?.postMessage({ type: 'closing', id: TAB_ID }) } catch {}
  }
})

if (channel) {
  const origOnMessage = channel.onmessage
  channel.onmessage = (e) => {
    if (e.data?.type === 'closing' && e.data?.id !== TAB_ID) {
      lastLeaderPing = 0
      setTimeout(claimLeadership, Math.random() * 300 + 100)
    }
    origOnMessage?.(e)
  }
}

export { shouldPoll, isLeader, isActive, isVisible, onBecomeActive }
export default shouldPoll
